import React, { FunctionComponent, useState } from 'react'

import styled, { useTheme } from 'styled-components/native'

import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { ToggleButtonsSelector } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/toggleButtonsSelector/ToggleButtonsSelector'
import { BodyTextSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

type PracticeAdminImpersonationBarProps = {
  defaultProviderView?: boolean
  onReturnToAdminView?: () => void
  title?: string
  logoItem?: React.JSX.Element[]
}

const Bar = styled.View<{ theme: ThemeType; backgroundColor?: string }>(({ theme: { colors }, backgroundColor }) => ({
  backgroundColor: backgroundColor ?? colors.backgroundPrimary,
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  borderBottomWidth: '1px',
  borderBottomColor: colors.borderDefault,
  height: '80px',
  zIndex: 7,
}))

const LeftGroup = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  paddingLeft: theme.spacing['32px'],
  paddingRight: theme.spacing['32px'],
}))

const CenterGroup = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'row',
  paddingLeft: theme.spacing['8px'],
  paddingRight: theme.spacing['8px'],
}))

const RightGroup = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'row',
  paddingLeft: theme.spacing['16px'],
  paddingRight: theme.spacing['16px'],
}))

const PracticeAdminImpersonationBarComponent: FunctionComponent<PracticeAdminImpersonationBarProps> = ({
  defaultProviderView = false,
  onReturnToAdminView,
  title = 'Practice Admin',
  logoItem,
}) => {
  const [isProviderView, setIsProviderView] = useState<boolean>(defaultProviderView)
  const { colors } = useTheme()

  const createViewText = (text: string, isSelected: boolean, key: string) => (
    <BodyText
      key={key}
      size={BodyTextSize.SMALL}
      color={isSelected ? colors.textInverse : colors.textPrimary}
      text={text}
    />
  )

  const adminViewText = createViewText('Admin view', !isProviderView, 'admin-view')
  const providerViewText = createViewText('Provider view', isProviderView, 'provider-view')

  const handleToggle = (index: number) => {
    const newValue = index === 1

    setIsProviderView(newValue)

    if (!newValue) {
      onReturnToAdminView?.()
    }
  }

  return (
    <Bar
      backgroundColor={isProviderView ? colors.backgroundHighlightPrimary : undefined}
      testID={tID('PracticeAdminImpersonationBar')}
    >
      <LeftGroup>
        <BodyText size={BodyTextSize.LARGE} text={title} />
      </LeftGroup>

      <CenterGroup>
        <ToggleButtonsSelector
          buttonsTexts={[adminViewText, providerViewText]}
          selectedIndex={isProviderView ? 1 : 0}
          onPress={handleToggle}
          customStyles={{ buttonHeight: '40px', containerMarginBottom: '0px' }}
        />
      </CenterGroup>

      <RightGroup>{isProviderView && logoItem}</RightGroup>
    </Bar>
  )
}

export const PracticeAdminImpersonationBar = React.memo(PracticeAdminImpersonationBarComponent)

export default PracticeAdminImpersonationBar
